<template>
 <!-- The new look (Seaded -> Kliendid uues kujunduses): the client as a plate
      with the töökäsud band under it, the objects, and the record, related
      clients and portal lock beside it. -->
 <div v-if="plate && client.id" class="obj-info">
  <div class="obj-info__main">
   <section class="obj-card obj-card--plate">
    <div class="obj-card__head obj-card__head--flat">
     <h2 class="obj-cap-title obj-mid">{{ client.type === 2 ? 'FIRMA' : 'ERAKLIENT' }}</h2>
     <router-link v-if="canEdit('client', client)" :to="{ name: 'client/edit', params: { clientId } }" class="obj-link obj-link--caps">MUUDA</router-link>
    </div>
    <div class="obj-fields">
     <div v-for="field in fields" :key="field.k" class="obj-field">
      <span class="obj-field__k">{{ field.k }}</span>
      <router-link v-if="field.to" :to="field.to" class="obj-field__v">{{ field.v }}</router-link>
      <span v-else class="obj-field__v" :class="{ 'obj-field__v--none': field.none }">{{ field.v }}</span>
     </div>
    </div>
    <div v-if="overview" class="obj-band cl-band">
     <span><span class="obj-cap">TÖÖKÄSKE</span><b>{{ overview.counts.events }}</b></span>
     <span><span class="obj-cap">ESIMENE</span><em>{{ day(overview.events.first) }}</em></span>
     <span><span class="obj-cap">VIIMANE</span><em>{{ overview.events.last ? day(overview.events.last.date) : '—' }}</em></span>
     <span><span class="obj-cap">MAKSMATA</span><b :class="{ 'obj-amber': overview.events.unpaid }">{{ overview.events.unpaid }}</b></span>
    </div>
   </section>

   <section class="obj-card">
    <div class="obj-card__head">
     <h2>Objektid <small v-if="overview">· {{ overview.objects.length }}</small></h2>
     <router-link :to="{ name: 'object/create', query: { clientId } }" class="obj-link obj-link--caps">+ LISA OBJEKT</router-link>
    </div>
    <div v-if="overview && !overview.objects.length" class="obj-empty">Kliendil pole objekte.</div>
    <router-link v-for="object in (overview ? overview.objects : [])" :key="object.id" :to="{ name: 'object/view/info', params: { objectId: object.id } }" class="cl-obj">
     <span class="obj-dot" :style="{ background: object.type !== 1 ? 'var(--p-ink-3)' : object.active ? 'var(--p-green)' : 'var(--p-line-strong)' }" />
     <span class="d-flex flex-column ga-1" style="min-width: 0">
      <span class="cl-obj__addr">{{ [object.address, object.city].filter(Boolean).join(', ') || 'Aadressita objekt' }}</span>
      <span class="cl-obj__meta">{{ objectMeta(object) }}</span>
     </span>
     <span class="cl-obj__n">{{ object.last ? 'viimati ' + day(object.last) : '' }}</span>
    </router-link>
   </section>

   <!-- Haldur (Seaded): the contract objects this client manages for another
        Klient; it logs in to the portal with their contract numbers too. -->
   <section v-if="overview && overview.managed && overview.managed.length" class="obj-card">
    <div class="obj-card__head">
     <h2>Objektid haldusfirmana <small>· {{ overview.managed.length }}</small></h2>
    </div>
    <router-link v-for="object in overview.managed" :key="object.id" :to="{ name: 'object/view/info', params: { objectId: object.id } }" class="cl-obj">
     <span class="obj-dot" :style="{ background: object.active ? 'var(--p-green)' : 'var(--p-line-strong)' }" />
     <span class="d-flex flex-column ga-1" style="min-width: 0">
      <span class="cl-obj__addr">{{ [object.address, object.city].filter(Boolean).join(', ') || 'Aadressita objekt' }}</span>
      <span class="cl-obj__meta">{{ ['Leping ' + (object.contract || '—'), object.client && object.client.fullName].filter(Boolean).join(' · ') }}</span>
     </span>
     <span class="cl-obj__n" />
    </router-link>
   </section>
  </div>

  <div class="obj-info__main">
   <section class="obj-card">
    <div class="obj-card__head"><h2>Kirje</h2></div>
    <dl class="cl-rec">
     <dt>LISAS</dt><dd>{{ client.adder && client.adder.fullName || '—' }}</dd>
     <dt>LISATUD</dt><dd>{{ dateTime(client.createdDate) }}</dd>
     <dt>MUUTIS</dt><dd>{{ client.editor && client.editor.fullName || '—' }}</dd>
     <dt>MUUDETUD</dt><dd>{{ dateTime(client.updatedDate) }}</dd>
    </dl>
   </section>

   <section v-if="relatedClients.length" class="obj-card">
    <div class="obj-card__head"><h2>Seotud kliendid <small>· {{ relatedClients.length }}</small></h2></div>
    <router-link v-for="related in relatedClients" :key="related.id" :to="{ name: 'client/view/info', params: { clientId: related.id } }" class="cl-related">{{ related.fullName }}</router-link>
   </section>

   <!-- Kliendiportaal: admin only, for clients with a contract, the only ones
        who can log in (service/portalLogin.js). -->
   <section v-if="portal && portal.contracts.length" class="obj-card">
    <div class="obj-card__head"><h2>Kliendiportaal</h2></div>
    <div v-for="row in portal.contracts" :key="row.contract" class="cl-portal">
     <span class="cl-portal__k">LEPING {{ row.contract }}</span>
     <span class="d-flex flex-wrap align-center ga-2">
      <span v-if="row.permanent" class="obj-pill obj-pill--late obj-pill--sm">Püsivalt lukus</span>
      <span v-else-if="row.locked" class="obj-pill obj-pill--late obj-pill--sm">Lukus kuni {{ lockedUntil(row.lockedUntil) }} ({{ row.level }}. kord)</span>
      <span v-else class="obj-pill obj-pill--ok obj-pill--sm"><i />Avatud</span>
      <span v-if="row.failures" class="cl-portal__sub">{{ row.failures }}/{{ portal.limits.contractMax }} valet katset järjest</span>
      <span v-else-if="row.level && !row.locked" class="cl-portal__sub">{{ row.level }} lukku järjest, järgmine on pikem</span>
     </span>
     <span v-if="row.lastFailure" class="cl-portal__sub">Viimane vale katse {{ dateTime(row.lastFailure.at) }}<template v-if="row.lastFailure.ip"> · {{ row.lastFailure.ip }}</template></span>
    </div>
    <div class="cl-portal">
     <span class="cl-portal__k">VIIMANE SISSELOGIMINE</span>
     <span>{{ portal.lastLogin ? dateTime(portal.lastLogin.at) : (client.lastLoginDate ? dateTime(client.lastLoginDate) : '—') }}</span>
    </div>
    <div v-if="portal.contracts.some(c => c.locked || c.failures || c.level)" class="obj-hint">
     <span>Lukk avab kõik selle kliendi lepingud.</span>
     <button type="button" class="obj-btn obj-btn--sm" :disabled="unlocking" @click="unlock()">Ava lukk</button>
    </div>
   </section>

   <!-- The portal Pääsuvõtmed this client added (Süsteem → Turvalisus). -->
   <passkeys-of v-if="isAdmin || isSecretary" kind="client" :owner-id="client.id" class="obj-card cl-keys" />
  </div>
 </div>
 <v-container fluid class="pa-0" v-else-if="client.id">
    <v-row>
      <v-col cols="12" md="6" class="pt-0">
        <v-card variant="flat" border>
          <v-list>
            <v-list-item>
              <v-list-item-subtitle>Nimi</v-list-item-subtitle>
              <v-list-item-title>{{client.fullName}}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Telefon</v-list-item-subtitle>
              <v-list-item-title>{{client.phone || '-'}}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Aadress</v-list-item-subtitle>
              <v-list-item-title>{{$address(client.fullAddress)}}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Postiindeks</v-list-item-subtitle>
              <v-list-item-title>{{client.postcode || '-'}}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Probleemne</v-list-item-subtitle>
              <v-list-item-title>{{client.problematic ? 'Jah' : 'Ei'}}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="client.problematic && client.problematicNote">
              <v-list-item-subtitle>Probleemi põhjus</v-list-item-subtitle>
              <div class="text-body-1" style="white-space: pre-line">{{client.problematicNote}}</div>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" class="pt-0 pb-0">
        <v-card variant="flat" border>
          <v-list>
            <v-list-item>
              <v-list-item-subtitle>Lisas</v-list-item-subtitle>
              <v-list-item-title>{{client.adder && client.adder.fullName}}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Lisatud</v-list-item-subtitle>
              <v-list-item-title>{{client.createdDate || '-'}}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Muutis</v-list-item-subtitle>
              <v-list-item-title>{{client.editor && client.editor.fullName}}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Muudetud</v-list-item-subtitle>
              <v-list-item-title>{{client.updatedDate || '-'}}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col v-if="relatedClients.length > 0" cols="12" md="6" class="pt-0 pb-0">
        <v-card variant="flat" border>
          <v-list>
            <v-list-item>
              <v-list-item-title class="text-h5">Seotud kliendid</v-list-item-title>
            </v-list-item>
            <v-list-item v-for="client in relatedClients" :key="client.id">
              <v-list-item-title>
                <router-link :to="{name: 'client/view/info', params: { clientId: client.id }}">{{client.fullName}}</router-link>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <!-- Kliendiportaal: whether the customer's login is locked after wrong
           guesses (service/portalLogin.js), and the way to open it. Admin
           only, like the staff lockout on Kasutajad; shown for clients with a
           contract, the only ones who can log in. -->
      <v-col v-if="portal && portal.contracts.length" cols="12" md="6" class="pt-0 pb-0">
        <v-card variant="flat" border>
          <v-list>
            <v-list-item>
              <v-list-item-title class="text-h5">Kliendiportaal</v-list-item-title>
            </v-list-item>
            <v-list-item v-for="row in portal.contracts" :key="row.contract">
              <v-list-item-subtitle>Leping {{row.contract}}</v-list-item-subtitle>
              <v-list-item-title>
                <v-chip v-if="row.permanent" color="red" size="x-small" variant="flat" class="mr-2">Püsivalt lukus</v-chip>
                <v-chip v-else-if="row.locked" color="red" size="x-small" variant="flat" class="mr-2">Lukus kuni {{lockedUntil(row.lockedUntil)}} ({{row.level}}. kord)</v-chip>
                <v-chip v-else color="green" size="x-small" variant="flat" class="mr-2">Avatud</v-chip>
                <span v-if="row.failures" class="text-caption">{{row.failures}}/{{portal.limits.contractMax}} valet katset järjest</span>
                <span v-else-if="row.level && !row.locked" class="text-caption">{{row.level}} lukku järjest, järgmine on pikem</span>
              </v-list-item-title>
              <div v-if="row.lastFailure" class="text-caption text-medium-emphasis">
                Viimane vale katse {{dateTime(row.lastFailure.at)}}<template v-if="row.lastFailure.ip"> · {{row.lastFailure.ip}}</template>
              </div>
            </v-list-item>
            <v-list-item>
              <v-list-item-subtitle>Viimane sisselogimine</v-list-item-subtitle>
              <v-list-item-title>{{portal.lastLogin ? dateTime(portal.lastLogin.at) : (client.lastLoginDate ? dateTime(client.lastLoginDate) : '-')}}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="portal.contracts.some(c => c.locked || c.failures || c.level)">
              <v-btn color="green" size="small" :loading="unlocking" @click="unlock()">Ava lukk</v-btn>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
 </v-container>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { mapGetters, mapActions } from 'vuex'
import helpersMixin from '@/plugins/helpersMixin'
import PasskeysOf from '@/components/PasskeysOf.vue'

export default {
  components: { PasskeysOf },
  mixins: [ helpersMixin ],
  props: {
    client: {
      required: true
    },
    // The new look (Seaded -> Kliendid uues kujunduses); overview is View.vue's.
    plate: {
      type: Boolean,
      default: false
    },
    overview: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      relatedClients: [],
      // GET /api/client/:id/portal-access, for admins.
      portal: null,
      unlocking: false
    }
  },
  watch: {
    async clientId () {
      this.refresh()
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'isSecretary']),
    clientId () {
      return this.$route.params.clientId
    },
    fields () {
      const c = this.client
      const parts = (c.fullAddress || '').split(', ')
      const none = v => !v || v === '-'
      return [
        // The name is the page's heading and Probleemne a pill beside it;
        // six fields fill two rows of three.
        { k: 'TELEFON', v: c.phone || '—', none: !c.phone },
        { k: 'E-POST', v: c.email || '—', none: !c.email },
        { k: 'AADRESS', v: none(c.address) ? '—' : c.address, none: none(c.address) },
        { k: 'LINN, RIIK', v: parts.slice(1).filter(Boolean).join(', ') || '—', none: !parts[1] },
        { k: 'POSTIINDEKS', v: c.postcode || '—', none: !c.postcode },
        c.contact && c.contact.id
          ? { k: 'KONTAKT', v: c.contact.fullName || 'Klient ' + c.contact.id, to: { name: 'client/view/info', params: { clientId: c.contact.id } } }
          : { k: 'KONTAKT', v: '—', none: true }
      ]
    }
  },
  methods: {
    refresh () {
      // Leaving the page (a delete sending the user to the list) clears the
      // route's clientId before this tab is gone.
      if (!this.clientId) {
        return
      }
      api.getRelatedClients(this.clientId).then(result => {
        this.relatedClients = result.clients
      })
      if (this.isAdmin) {
        api.getClientPortalAccess(this.clientId).then(result => {
          this.portal = result
        }).catch(() => {
          this.portal = null
        })
      }
    },
    ...mapActions(['showNotification']),
    async unlock () {
      this.unlocking = true
      try {
        this.portal = await api.unlockClientPortal(this.clientId)
        this.showNotification({ text: 'Kliendiportaali lukk avatud', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.unlocking = false
      }
    },
    day (date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : '—'
    },
    objectMeta (object) {
      const kind = object.type === 1 ? 'Lepinguline' + (object.contract ? ' ' + object.contract : '') : 'Tavaline'
      const events = object.events + ' ' + (object.events === 1 ? 'töökäsk' : 'töökäsku')
      // Aktiivne is a contract's state; an ordinary object has none.
      return [kind, object.type === 1 && !object.active ? 'mitteaktiivne' : null, events].filter(Boolean).join(' · ')
    },
    time (date) {
      return date ? dayjs(date).format('HH:mm') : ''
    },
    // A 24-hour lock ends on another day: then the date too.
    lockedUntil (date) {
      if (!date) return ''
      return dayjs(date).isSame(dayjs(), 'day') ? dayjs(date).format('HH:mm') : dayjs(date).format('DD.MM HH:mm')
    },
    dateTime (date) {
      return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-'
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>